// Copyright ©️ 2026 eVolve MEP, LLC

import { useLayoutEffect, useRef } from 'react';

import { isNotNil } from 'helpers/isNotNil';

export type NumberInputProps = {
  id: string;
  unitOfMeasure: string;
  min: number;
  max: number;
  step: number;
  defaultValue: string;
  className?: string;
};

export const NumberInput = ({ id, unitOfMeasure, min, max, step, defaultValue, className }: NumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const incrementButtonRef = useRef<HTMLButtonElement>(null);
  const decrementButtonRef = useRef<HTMLButtonElement>(null);

  // Native event listeners are used instead of React synthetic event handlers (e.g. onClick) because
  // BaseMarkupTool moves this component's root DOM node out of the React root container after rendering,
  // which breaks React's event delegation. useLayoutEffect (vs useEffect) ensures the listeners are
  // attached synchronously before BaseMarkupTool extracts the element.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const label = labelRef.current;
    const incrementButton = incrementButtonRef.current;
    const decrementButton = decrementButtonRef.current;

    const stopPropagation = (e: Event) => e.stopPropagation();

    const handleChange = (delta: number) => {
      if (!input) return;
      const current = parseFloat(input.value) || 0;
      const next = Math.min(max, Math.max(min, Math.round(current + delta)));
      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    let repeatTimeout: ReturnType<typeof setTimeout> | null = null;
    let repeatInterval: ReturnType<typeof setInterval> | null = null;

    /**
     * Long press an arrow button to trigger a high velocity change in value after an initial delay,
     * similar to native number inputs.
     * */
    const startRepeat = (delta: number) => {
      const INITIAL_DELAY_IN_MS = 500;
      const REPEAT_INTERVAL_IN_MS = 50;

      repeatTimeout = setTimeout(() => {
        repeatInterval = setInterval(() => handleChange(delta), REPEAT_INTERVAL_IN_MS);
      }, INITIAL_DELAY_IN_MS);
    };

    const stopRepeat = () => {
      if (isNotNil(repeatTimeout)) {
        clearTimeout(repeatTimeout);
        repeatTimeout = null;
      }
      if (isNotNil(repeatInterval)) {
        clearInterval(repeatInterval);
        repeatInterval = null;
      }
      input?.dispatchEvent(new Event('blur', { bubbles: true }));
    };

    const handleIncrementPointerDown = (e: PointerEvent) => {
      e.stopPropagation();
      handleChange(step);
      startRepeat(step);
      incrementButton?.setPointerCapture(e.pointerId);
    };

    const handleDecrementPointerDown = (e: PointerEvent) => {
      e.stopPropagation();
      handleChange(-step);
      startRepeat(-step);
      decrementButton?.setPointerCapture(e.pointerId);
    };

    const handlePointerUp = (e: Event) => {
      e.stopPropagation();
      stopRepeat();
    };

    /**
     * Move the cursor to the end of the input when clicking the label.
     */
    const handleLabelClick = (e: Event) => {
      if (!input || e.target === input) return;
      // Hack: Switching to 'text' allows setting the selection range, which is used to reliably move the cursor.
      input.type = 'text';
      input.setSelectionRange(-1, -1);
      input.type = 'number';
    };

    /**
     * These are the unhandled events that need to stop their propagation to prevent the Autodesk viewer from capturing
     * them, otherwise the viewer will intercept events and cause spinners to increment/decrement indefinitely.
     * */
    const stopEvents = ['mousedown', 'mouseup', 'click'] as const;

    // Add event listeners.
    for (const button of [incrementButton, decrementButton]) {
      for (const event of stopEvents) button?.addEventListener(event, stopPropagation);
      button?.addEventListener('pointerup', handlePointerUp);
    }
    incrementButton?.addEventListener('pointerdown', handleIncrementPointerDown);
    decrementButton?.addEventListener('pointerdown', handleDecrementPointerDown);
    label?.addEventListener('click', handleLabelClick);

    // Remove event listeners.
    return () => {
      stopRepeat();
      for (const button of [incrementButton, decrementButton]) {
        for (const event of stopEvents) button?.removeEventListener(event, stopPropagation);
        button?.removeEventListener('pointerup', stopRepeat);
      }
      incrementButton?.removeEventListener('pointerdown', handleIncrementPointerDown);
      decrementButton?.removeEventListener('pointerdown', handleDecrementPointerDown);
      label?.removeEventListener('click', handleLabelClick);
    };
  }, [min, max, step]);

  return (
    <label ref={labelRef} className={`number-input-wrapper ${className ?? ''}`}>
      <input
        ref={inputRef}
        id={id}
        name={id}
        type="number"
        min={min}
        max={max}
        step={step}
        defaultValue={defaultValue}
      />
      <span className="number-input-uom">{unitOfMeasure}</span>
      <div className="number-input-spinner-buttons">
        <button ref={incrementButtonRef} type="button" className="spinner-button" tabIndex={-1}>
          <div className="adsk-icon-arrow" />
        </button>
        <button ref={decrementButtonRef} type="button" className="spinner-button" tabIndex={-1}>
          <div className="adsk-icon-arrow" />
        </button>
      </div>
    </label>
  );
};
